import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Circle, Phone, Clock, Loader2, PartyPopper, Navigation, MessageCircle } from "lucide-react";
import api, { money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import OrderChatModal from "@/components/OrderChatModal";

const FLOW_DELIVERY = [
  { key: "NEW", label: "Order Received" },
  { key: "PREPARING", label: "Being Prepared" },
  { key: "READY", label: "Ready" },
  { key: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
  { key: "COMPLETED", label: "Delivered" },
];
const FLOW_COLLECTION = [
  { key: "NEW", label: "Order Received" },
  { key: "PREPARING", label: "Being Prepared" },
  { key: "READY", label: "Ready for Collection" },
  { key: "COMPLETED", label: "Collected" },
];
const RANK = { NEW: 0, ACCEPTED: 0, PREPARING: 1, READY: 2, OUT_FOR_DELIVERY: 3, COMPLETED: 99 };

export default function OrderTrack() {
  const { orderNumber } = useParams();
  const { settings } = useSettings();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/orders/${orderNumber}`); setOrder(data); }
    catch { setErr(true); }
  }, [orderNumber]);

  const loadUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/chat/my-order-threads");
      const t = (data || []).find((x) => x.order_number === orderNumber);
      setUnread(t ? t.unread : 0);
    } catch (e) { console.warn("Failed to load unread chat count", e); }
  }, [user, orderNumber]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { loadUnread(); const t = setInterval(loadUnread, 12000); return () => clearInterval(t); }, [loadUnread]);
  // Points may have changed on this order — refresh the header balance.
  useEffect(() => { if (user) refresh(); }, [orderNumber]); // eslint-disable-line

  if (err) return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="order-not-found">
      <p className="font-heading font-bold text-2xl">Order not found</p>
      <p className="text-muted-foreground mt-1">Check your order number and try again.</p>
      <button onClick={() => navigate("/")} className="mt-4 bg-metros-red text-white rounded-full px-6 py-3 font-semibold">Back home</button>
    </div>
  );
  if (!order) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-metros-red" size={32} /></div>;

  const flow = order.type === "delivery" ? FLOW_DELIVERY : FLOW_COLLECTION;
  const cancelled = order.status === "CANCELLED";
  const currentRank = RANK[order.status] ?? 0;

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
      <div className="text-center mb-8">
        <div className="inline-flex bg-green-100 text-green-600 rounded-full p-3 mb-3"><PartyPopper size={30} /></div>
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl" data-testid="order-confirmed">Order Confirmed</h1>
        <p className="text-muted-foreground mt-1">Thanks for ordering from METROS!</p>
        <p className="font-heading font-bold text-metros-red text-xl mt-2" data-testid="order-number">{order.order_number}</p>
      </div>

      <div className="bg-metros-black text-white grain rounded-2xl p-6 mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-widest">Estimated {order.type === "delivery" ? "delivery" : "collection"} time</p>
          <p className="font-heading font-extrabold text-2xl mt-1 flex items-center gap-2"><Clock size={22} className="text-metros-red" /> {order.estimated_time}</p>
          <p className="text-xs text-gray-400 mt-2" data-testid="order-placed-time">Placed: {new Date(order.created_at).toLocaleString("en-GB")}</p>
          {order.collection_time && order.collection_time !== "asap" && (
            <p className="text-sm font-bold text-white mt-1" data-testid="order-scheduled-time">Scheduled {order.type === "delivery" ? "delivery" : "collection"}: {order.collection_time}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <button data-testid="track-chat-btn" onClick={() => { setShowChat(true); setUnread(0); }}
              className="relative bg-white/10 hover:bg-metros-red rounded-full p-3 transition-colors" title="Chat about this order">
              <MessageCircle size={20} />
              {unread > 0 && <span data-testid="track-chat-unread" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-metros-red text-white text-[10px] font-extrabold flex items-center justify-center border border-white animate-pulse">{unread}</span>}
            </button>
          )}
          <a href={`tel:${settings?.phone}`} className="bg-white/10 hover:bg-metros-red rounded-full p-3 transition-colors" data-testid="contact-restaurant"><Phone size={20} /></a>
        </div>
      </div>

      {cancelled ? (
        <div className="bg-red-50 text-metros-red rounded-2xl p-6 text-center font-semibold" data-testid="order-cancelled">This order was cancelled. Please contact us if you need help.</div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-6" data-testid="tracking-timeline">
          <h2 className="font-heading font-bold text-lg mb-4">Order status</h2>
          <div className="space-y-1">
            {flow.map((s, i) => {
              const done = currentRank >= RANK[s.key] || order.status === "COMPLETED";
              const active = RANK[order.status] === RANK[s.key] || (order.status === "COMPLETED" && i === flow.length - 1);
              return (
                <div key={s.key} className="flex items-center gap-3">
                  <div className="flex flex-col items-center">
                    {done ? <CheckCircle2 className="text-metros-red" size={24} /> : <Circle className="text-gray-300" size={24} />}
                    {i < flow.length - 1 && <div className={`w-0.5 h-8 ${done ? "bg-metros-red" : "bg-gray-200"}`} />}
                  </div>
                  <span className={`text-sm ${active ? "font-bold text-metros-black" : done ? "text-metros-black" : "text-gray-400"}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-100 rounded-2xl p-6">
        <h2 className="font-heading font-bold text-lg mb-3">Order summary</h2>
        <div className="space-y-2">
          {order.items.map((it, i) => (
            <div key={i} className="text-sm">
              <div className="flex justify-between">
                <span>{it.quantity}× {it.name}</span><span className="font-semibold">{money(it.line_total)}</span>
              </div>
              {(it.options || []).map((op, oi) => (
                <div key={oi} className="flex justify-between text-xs text-muted-foreground pl-4">
                  <span>+ {op.name}</span><span>{money(op.price || 0)}</span>
                </div>
              ))}
              {it.special_instructions && <p className="text-xs italic text-muted-foreground pl-4">“{it.special_instructions}”</p>}
            </div>
          ))}
        </div>
        <div className="border-t border-gray-100 mt-3 pt-3 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(order.subtotal)}</span></div>
          {order.type === "delivery" && <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{order.delivery_fee === 0 ? "FREE" : money(order.delivery_fee)}</span></div>}
          {order.discount > 0 && <div className="flex justify-between text-metros-red"><span>Discount</span><span>-{money(order.discount)}</span></div>}
          <div className="flex justify-between font-heading font-extrabold text-lg"><span>Total</span><span>{money(order.total)}</span></div>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <Link to="/menu" className="flex-1 text-center bg-metros-red text-white rounded-full py-3.5 font-bold">Order more</Link>
        <Link to="/orders" className="flex-1 text-center border border-gray-200 rounded-full py-3.5 font-semibold">My orders</Link>
      </div>
      {(settings?.address || settings?.name) && (
        <a data-testid="get-directions" target="_blank" rel="noopener noreferrer"
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings?.address || settings?.name)}`}
          className="mt-3 flex items-center justify-center gap-2 border border-gray-200 rounded-full py-3.5 font-semibold text-metros-black hover:border-metros-red hover:text-metros-red transition-colors">
          <Navigation size={16} /> Get Directions
        </a>
      )}
      {showChat && user && <OrderChatModal order={order} viewer="customer" onClose={() => { setShowChat(false); loadUnread(); }} />}
    </div>
  );
}
